import { AdminLinkButton } from "@/admin/AdminLinkButton.tsx";
import { Missing, Uuid } from "@/admin/AdminTable.tsx";
import { AdminTitle } from "@/admin/AdminTitle.tsx";
import { adminStoragePoolQueryAtom } from "@/admin/buckets/storagePoolsQuery.ts";
import { AddHostPoolButton, EditHostPoolButton } from "@/admin/host_pools/AddHostPoolModal.tsx";
import {
    adminHostPoolQueryAtom,
    type AdminHostPoolWithType,
    hostPoolDeleteMutation,
} from "@/admin/host_pools/hostPoolsQuery.ts";
import { ToggleDefaultDesignatedModal } from "@/admin/host_pools/ToggleDefaultDesignatedModal.tsx";
import { ToggleHvAutoAssignedModal } from "@/admin/host_pools/ToggleHvAutoAssignedModal.tsx";
import { ToggleVisibleModal } from "@/admin/host_pools/ToggleVisibleModal.tsx";
import { LocationItem } from "@/admin/table_fields/LocationItem.tsx";
import { WAdminTable } from "@/admin/WAdminTable.tsx";
import { link } from "@/components/Action.tsx";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { WFakeCheckBox } from "@/components/forms/WFakeCheckBox.tsx";
import { WFakeRadio } from "@/components/forms/WFakeRadio.tsx";
import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { WTableBody } from "@/components/table/WTable.tsx";
import { InfoTooltip } from "@/components/WTooltip.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { ErrorRow } from "@/modules/error/ErrorFallback.tsx";
import { queryAtom } from "@/modules/location/locationQuery.ts";
import { plural } from "@/utils/plural.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useAtomValue } from "jotai/react";

export function HostPoolsView() {
    const locations = useSuspenseQueryAtom(queryAtom);

    const table = (
        <WAdminTable>
            <thead>
                <tr>
                    <th>UI pos</th>
                    <th>Name</th>
                    <th>UUID</th>
                    <th>Description</th>
                    <th>CPU model</th>
                    <th className="text-right">CPU coef.</th>
                    <th className="text-right">Mem coef.</th>
                    <th>VM limits</th>
                    <th>
                        <div className="flex justify-center">
                            Default pool for VMs
                            <InfoTooltip className="ml-1" text="Default designated pool for new virtual machines." />
                        </div>
                    </th>
                    <th>
                        <div className="flex justify-center">
                            Pool is visible
                            <InfoTooltip className="ml-1" text="Pool visibility for end-users." />
                        </div>
                    </th>
                    <th>Hosts</th>
                    <th>
                        <div className="flex justify-center">
                            Default pool for hosts
                            <InfoTooltip
                                className="ml-1"
                                text="New hosts added to the cluster will be assigned to the default pool."
                            />
                        </div>
                    </th>
                    <th>Storage Pool</th>
                    <th>Storage Pool UUID</th>
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {locations.map(({ slug }) => (
                    <HostPoolRow key={slug} location={slug} />
                ))}
            </WTableBody>
        </WAdminTable>
    );

    return (
        <>
            <AdminTitle title="Hypervisor Pools">
                <AddHostPoolButton button={<WModalButton label="New" color="primary" size="bar" variant="basic" />} />
            </AdminTitle>
            {table}
            <div className="p-2">
                <AddHostPoolButton
                    button={
                        <WModalButton
                            className="w-full"
                            label="Add Hypervisor Pool"
                            color="primary"
                            size="lg"
                            variant="dashed"
                            icon="jp-icon-add"
                        />
                    }
                />
            </div>
        </>
    );
}

function HostPoolRow({ location }: { location: string }) {
    const { error: hostError, data: hostPools } = useAtomValue(adminHostPoolQueryAtom(location));
    const { error: storageError, data: storagePools } = useAtomValue(adminStoragePoolQueryAtom(location));
    const deleteMutation = useStandardMutation(hostPoolDeleteMutation);

    async function onDelete(item: AdminHostPoolWithType) {
        await deleteMutation.mutateAsync({ uuid: item.uuid, location: item.location });
    }

    return (
        <>
            <tr>
                <td colSpan={99} className="text-center bg-grey-1">
                    <LocationItem value={location} />
                </td>
            </tr>
            {hostError || storageError ? (
                <tr>
                    <td colSpan={99} className="text-left bg-error-light p-0!">
                        {hostError && <ErrorRow error={hostError} tags="HostPoolsTable, hostPools" />}
                        {storageError && <ErrorRow error={storageError} tags="HostPoolsTable, storagePools" />}
                    </td>
                </tr>
            ) : (
                !!hostPools?.size &&
                [...hostPools.values()].map((item) => {
                    const {
                        ui_position,
                        name,
                        uuid,
                        description,
                        cpu_model,
                        cpu_allocate_coefficient,
                        mem_allocate_coefficient,
                        is_default_designated,
                        is_visible,
                        hosts_assigned_count,
                        is_hv_auto_assigned,
                        storage_pool_uuid,
                        guest_limits,
                    } = item;

                    const storagePool = storagePools?.get(storage_pool_uuid);

                    return (
                        <tr key={uuid}>
                            <td>{ui_position}</td>
                            <td>{name}</td>
                            <td>
                                <ClipBoardTooltip isHtml hideIcon>
                                    <Uuid value={uuid} />
                                </ClipBoardTooltip>
                            </td>
                            <td>{description}</td>
                            <td>{cpu_model}</td>
                            <td className="text-right">{cpu_allocate_coefficient}</td>
                            <td className="text-right">{mem_allocate_coefficient}</td>
                            <td className="whitespace-nowrap">
                                <div>
                                    CPU: {guest_limits?.cpu.min} - {guest_limits?.cpu.max}
                                </div>
                                <div>
                                    RAM: {guest_limits?.ram_mb.min} - {guest_limits?.ram_mb.max} MB
                                </div>
                                <div>
                                    DISK: {guest_limits?.disk_gb.min} - {guest_limits?.disk_gb.max} GB
                                </div>
                            </td>
                            <td>
                                <div className="flex justify-center">
                                    <ToggleDefaultDesignatedModal
                                        button={<WFakeRadio isSelected={is_default_designated} />}
                                        item={item}
                                    />
                                </div>
                            </td>
                            <td>
                                <div className="flex justify-center">
                                    <ToggleVisibleModal
                                        button={<WFakeCheckBox isSelected={is_visible} />}
                                        item={item}
                                    />
                                </div>
                            </td>
                            <td>
                                {hosts_assigned_count ? (
                                    <AdminLinkButton
                                        action={link({
                                            to: "/admin/hosts",
                                            search: { hostPoolId: item.uuid },
                                        })}
                                        label={plural(hosts_assigned_count, "host", "hosts")}
                                    />
                                ) : (
                                    <Missing />
                                )}
                            </td>
                            <td>
                                <div className="flex justify-center">
                                    <ToggleHvAutoAssignedModal
                                        button={<WFakeRadio isSelected={is_hv_auto_assigned} />}
                                        item={item}
                                    />
                                </div>
                            </td>
                            <td>{storagePool?.display_name}</td>
                            <td>
                                <ClipBoardTooltip isHtml hideIcon>
                                    <Uuid value={storage_pool_uuid} />
                                </ClipBoardTooltip>
                            </td>
                            <td className="text-right">
                                <div className="flex gap-0.5em justify-end">
                                    <EditHostPoolButton
                                        item={item}
                                        button={<WModalButton label="Edit" inTable icon="jp-icon-edit" />}
                                    />

                                    <DeleteModal
                                        isDisabled={item.is_hv_auto_assigned || !!item.hosts_assigned_count}
                                        inTable
                                        modalAction={async () => await onDelete(item)}
                                    >
                                        Deleting the host pool "<b>{name}</b>" will permanently remove it.
                                    </DeleteModal>
                                </div>
                            </td>
                        </tr>
                    );
                })
            )}
        </>
    );
}

// cspell:words coef
